import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Menu, ChevronDown, MessageCircle, Instagram, Facebook, Truck, User } from "lucide-react";
import { api, getCategories, trackWaClick } from "@/lib/api";
import { buildWhatsAppUrl, fmtBRL, buildProductInterestMessage } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AnnouncementBar } from "./AnnouncementBar";

export function useCategoryTree() {
  const [tree, setTree] = useState([]);
  useEffect(() => {
    // cache compartilhado: uma única requisição de categorias por visita
    getCategories().then((cats) => {
      const roots = cats.filter((c) => !c.parent_id && c.show_in_menu !== false);
      setTree(
        roots.map((r) => ({
          ...r,
          subs: cats.filter((c) => c.parent_id === r.id && c.show_in_menu !== false),
        }))
      );
    }).catch(() => {});
  }, []);
  return tree;
}

// lista de autocompletar: foto, nome, categoria e preço — clique vai direto ao produto
const SuggestDropdown = ({ suggest, query, onClose }) => {
  const settings = useSettings();
  const navigate = useNavigate();
  const hasProducts = suggest.products?.length > 0;
  const hasCats = suggest.categories?.length > 0;
  if (!hasProducts && !hasCats) {
    return (
      <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 p-4 text-sm text-gray-500 z-50" data-testid="search-suggest-empty">
        Nada encontrado para "{query}" — tente outra palavra.
      </div>
    );
  }
  return (
    <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="search-suggest">
      {hasCats && (
        <div className="px-3 pt-3 pb-1 flex flex-wrap gap-1.5">
          {suggest.categories.map((c) => (
            <Link
              key={c.slug}
              to={`/categoria/${c.slug}`}
              onClick={onClose}
              className="text-xs font-semibold bg-blue-50 text-brand rounded-full px-3 py-1.5 hover:bg-brand hover:text-white transition-colors"
              data-testid={`suggest-cat-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      )}
      {suggest.products.map((p) => (
        <div
          key={p.id}
          role="link"
          tabIndex={0}
          onClick={() => { onClose(); navigate(`/produto/${p.id}`); }}
          onKeyDown={(e) => e.key === "Enter" && (onClose(), navigate(`/produto/${p.id}`))}
          className="flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50/60 transition-colors border-t border-gray-50 first:border-0 cursor-pointer"
          data-testid={`suggest-product-${p.id}`}
        >
          {p.image ? (
            <img src={imgVariant(p.image, "thumb")} alt="" className="w-10 h-10 rounded-lg object-contain bg-gray-50 shrink-0" loading="lazy" decoding="async" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
            <p className="text-xs text-gray-500 truncate">
              {p.category_name}
              {p.colors?.length > 0 ? ` • ${p.colors.join(", ")}` : ""}
              {p.stock <= 0 ? " • Indisponível" : ""}
            </p>
          </div>
          <span className="text-sm font-bold text-[#D71920] shrink-0">{fmtBRL(p.price)}</span>
          <a
            href={buildWhatsAppUrl(
              buildProductInterestMessage({ product: p, color: p.colors?.[0], domain: settings.company_domain }),
              settings.whatsapp_number
            )}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => { e.stopPropagation(); trackWaClick(p.id, "busca"); }}
            aria-label={`Comprar ${p.name} pelo WhatsApp`}
            data-testid={`suggest-wa-${p.id}`}
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white hover:bg-green-700"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </a>
        </div>
      ))}
      <Link
        to={`/produtos?busca=${encodeURIComponent(query)}`}
        onClick={onClose}
        className="block text-center text-xs font-bold text-brand py-2.5 border-t border-gray-100 hover:bg-blue-50"
        data-testid="suggest-see-all"
      >
        Ver todos os resultados
      </Link>
    </div>
  );
};

export const Header = () => {
  const { count } = useCart();
  const settings = useSettings();
  const navigate = useNavigate();
  const categories = useCategoryTree();
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  // busca instantânea com autocompletar (com tolerância a erros de digitação no servidor)
  const [suggest, setSuggest] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const suggestTimer = useRef(null);

  const onSearchChange = (value) => {
    setSearch(value);
    clearTimeout(suggestTimer.current);
    if (!value.trim()) {
      setSuggest(null);
      setSuggestOpen(false);
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q: value.trim(), limit: 8 } });
        setSuggest(data);
        setSuggestOpen(true);
      } catch {
        /* sem sugestões */
      }
    }, 250);
  };

  const closeSuggest = () => setSuggestOpen(false);

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      closeSuggest();
      navigate(`/produtos?busca=${encodeURIComponent(search.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50" data-testid="store-header">
      <AnnouncementBar />
      {/* barra institucional */}
      <div className="bg-[#07335C] text-white" data-testid="header-topbar">
        <div className="container-store flex items-center justify-between gap-3 py-1.5 text-[11px] sm:text-xs">
          <p className="flex items-center gap-1.5 min-w-0">
            <Truck className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
            <span className="truncate">
              Entrega em até 24h*{" "}
              <span className="hidden sm:inline text-blue-200">
                *sob disponibilidade da região e confirmação da equipe
              </span>
            </span>
          </p>
          <div className="flex items-center gap-2.5 shrink-0">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram da HG Móveis"
                className="text-blue-200 hover:text-yellow-400 transition-colors"
                data-testid="header-instagram"
              >
                <Instagram className="w-3.5 h-3.5" />
              </a>
            )}
            {settings.facebook_url && (
              <a
                href={settings.facebook_url}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook da HG Móveis"
                className="text-blue-200 hover:text-yellow-400 transition-colors"
                data-testid="header-facebook"
              >
                <Facebook className="w-3.5 h-3.5" />
              </a>
            )}
            <a
              href={buildWhatsAppUrl("Olá! Vim pelo site da HG Móveis e quero saber mais.", settings.whatsapp_number)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 font-semibold text-green-400 hover:text-green-300 transition-colors"
              data-testid="header-whatsapp-link"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {settings.whatsapp_display}
            </a>
          </div>
        </div>
      </div>

      {/* barra principal */}
      <div className="bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="container-store flex items-center gap-3 sm:gap-5 py-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto" data-testid="mobile-menu" data-anim="menu">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <img src="/assets/logo.png" alt="HG Móveis" className="h-10 w-10" />
                  HG Móveis
                </SheetTitle>
              </SheetHeader>
              <form onSubmit={doSearch} className="mt-4 flex gap-2">
                <Input
                  data-testid="mobile-search-input"
                  placeholder="Buscar móveis..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button type="submit" size="icon" data-testid="mobile-search-submit">
                  <Search className="w-4 h-4" />
                </Button>
              </form>
              <nav className="mt-6 space-y-1">
                <Link
                  to="/produtos"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-lg font-semibold text-gray-800 hover:bg-blue-50 hover:text-brand"
                  data-testid="mobile-nav-all"
                >
                  Todos os Produtos
                </Link>
                <Link
                  to="/produtos?promo=1"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-lg font-semibold text-amber-600 hover:bg-amber-50"
                  data-testid="mobile-nav-ofertas"
                >
                  Ofertas
                </Link>
                <Link
                  to="/conta"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-lg font-semibold text-gray-800 hover:bg-blue-50 hover:text-brand"
                  data-testid="mobile-nav-account"
                >
                  Minha Conta
                </Link>
                <Link
                  to="/pedidos"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-lg font-semibold text-gray-800 hover:bg-blue-50 hover:text-brand"
                  data-testid="mobile-nav-my-orders"
                >
                  Meus Pedidos
                </Link>
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <div className="flex items-center">
                      <Link
                        to={`/categoria/${cat.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex-1 px-3 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-blue-50 hover:text-brand"
                        data-testid={`mobile-nav-cat-${cat.slug}`}
                      >
                        {cat.name}
                      </Link>
                      {cat.subs.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                          aria-expanded={openCat === cat.id}
                          aria-label={`${openCat === cat.id ? "Recolher" : "Expandir"} subcategorias de ${cat.name}`}
                          className="p-2.5 rounded-lg text-gray-400 hover:text-brand hover:bg-blue-50 transition-colors"
                          data-testid={`mobile-nav-toggle-${cat.slug}`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${openCat === cat.id ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </div>
                    {openCat === cat.id &&
                      cat.subs.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/categoria/${sub.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="block pl-8 pr-3 py-1.5 text-sm text-gray-500 hover:text-brand"
                          data-testid={`mobile-nav-sub-${sub.slug}`}
                        >
                          {sub.name}
                        </Link>
                      ))}
                  </div>
                ))}
              </nav>
              {(settings.instagram_url || settings.facebook_url) && (
                <div className="mt-8 pt-4 border-t border-gray-100">
                  <p className="px-3 text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    Siga a HG Móveis
                  </p>
                  <div className="flex gap-2 px-3">
                    {settings.instagram_url && (
                      <a
                        href={settings.instagram_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Instagram da HG Móveis"
                        className="p-2.5 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white"
                        data-testid="mobile-menu-instagram"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                    {settings.facebook_url && (
                      <a
                        href={settings.facebook_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Facebook da HG Móveis"
                        className="p-2.5 rounded-full bg-[#1877F2] text-white"
                        data-testid="mobile-menu-facebook"
                      >
                        <Facebook className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 shrink-0" data-testid="header-logo">
            <img src="/assets/logo.png" alt="HG Móveis" className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20" />
            <span className="hidden sm:block font-heading font-extrabold text-xl lg:text-2xl text-brand leading-tight tracking-tight">
              HG Móveis
              <span className="block text-[11px] font-body font-medium text-gray-500 tracking-wide">
                {settings.store_slogan}
              </span>
            </span>
          </Link>

          <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-xl mx-auto">
            <div className="relative w-full">
              <Input
                data-testid="search-input"
                placeholder="Busque por sofá, guarda-roupa, cama..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => suggest && setSuggestOpen(true)}
                onBlur={() => setTimeout(closeSuggest, 200)}
                className="pr-12 h-12 rounded-full border-gray-200 bg-gray-50 focus:bg-white text-base"
              />
              <Button
                type="submit"
                size="icon"
                className="absolute right-1.5 top-1.5 h-9 w-9 rounded-full"
                data-testid="search-submit-button"
              >
                <Search className="w-4 h-4" />
              </Button>
              {suggestOpen && suggest && <SuggestDropdown suggest={suggest} query={search} onClose={closeSuggest} />}
            </div>
          </form>

          <div className="flex items-center gap-2 ml-auto">
            <a
              href={buildWhatsAppUrl("Olá! Vim pelo site da HG Móveis e quero saber mais.", settings.whatsapp_number)}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded-full px-4 py-2.5 transition-colors"
              data-testid="header-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" />
              Fale Conosco
            </a>
            <Link to="/conta">
              <Button variant="ghost" size="icon" className="h-11 w-11" title="Minha Conta" data-testid="account-button">
                <User className="w-6 h-6" />
              </Button>
            </Link>
            <Link to="/carrinho">
              <Button variant="ghost" size="icon" className="relative h-11 w-11" data-testid="cart-button">
                <ShoppingCart className="w-6 h-6" />
                {count > 0 && (
                  <span
                    className="absolute -top-1 -right-1 bg-secondary text-slate-900 text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                    data-testid="cart-count-badge"
                  >
                    {count}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>

        {/* busca mobile */}
        <div className="md:hidden container-store pb-3">
          <form onSubmit={doSearch} className="relative">
            <Input
              data-testid="mobile-search-bar"
              placeholder="Busque por sofá, guarda-roupa..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => suggest && setSuggestOpen(true)}
              onBlur={() => setTimeout(closeSuggest, 200)}
              className="pr-11 h-11 rounded-full border-gray-200 bg-gray-50"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand"
              data-testid="mobile-search-bar-submit"
            >
              <Search className="w-4 h-4" />
            </button>
            {suggestOpen && suggest && <SuggestDropdown suggest={suggest} query={search} onClose={closeSuggest} />}
          </form>
        </div>
      </div>

      {/* navegação de categorias (desktop) — barra azul institucional */}
      <nav className="hidden lg:block bg-[#07335C] shadow-md" data-testid="category-nav">
        <div className="container-store flex items-center gap-1">
          <Link
            to="/produtos"
            className="px-3.5 py-3 text-[15px] font-semibold text-blue-100 hover:text-yellow-400 transition-colors"
            data-testid="nav-all-products"
          >
            Todos os Produtos
          </Link>
          <Link
            to="/produtos?promo=1"
            className="px-3.5 py-3 text-[15px] font-bold text-yellow-400 hover:text-yellow-300 transition-colors"
            data-testid="nav-ofertas"
          >
            Ofertas
          </Link>
          <Link
            to="/pedidos"
            className="px-3.5 py-3 text-[15px] font-semibold text-blue-100 hover:text-yellow-400 transition-colors"
            data-testid="nav-my-orders"
          >
            Meus Pedidos
          </Link>
          {categories.map((cat) =>
            cat.subs.length > 0 ? (
              /* submenu por hover (desktop) — abre ao passar o mouse, sem clique */
              <div key={cat.id} className="relative group">
                <Link
                  to={`/categoria/${cat.slug}`}
                  className="flex items-center gap-1 px-3.5 py-3 text-[15px] font-medium text-blue-100 hover:text-yellow-400 transition-colors"
                  data-testid={`nav-cat-${cat.slug}`}
                  aria-haspopup="true"
                >
                  {cat.name}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-150 group-hover:rotate-180" />
                </Link>
                <div data-anim="menu" className="absolute left-0 top-full pt-1.5 z-50 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-150">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[230px] overflow-hidden">
                    <Link
                      to={`/categoria/${cat.slug}`}
                      className="block px-4 py-2 text-sm font-semibold text-brand hover:bg-brand-light transition-colors"
                      data-testid={`nav-cat-${cat.slug}-all`}
                    >
                      Ver todos em {cat.name}
                    </Link>
                    <div className="my-1 border-t border-slate-50" />
                    {cat.subs.map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/categoria/${sub.slug}`}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-light hover:text-brand transition-colors"
                        data-testid={`nav-sub-${sub.slug}`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={cat.id}
                to={`/categoria/${cat.slug}`}
                className="px-3.5 py-3 text-[15px] font-medium text-blue-100 hover:text-yellow-400 transition-colors"
                data-testid={`nav-cat-${cat.slug}`}
              >
                {cat.name}
              </Link>
            )
          )}
        </div>
      </nav>
    </header>
  );
};
